import React from 'react';

import type { EventHandlers } from '@atlaskit/editor-common/EventHandlers';
import { Mention as EditorCommonMention } from '@atlaskit/editor-common/mention';
import { type ProviderFactory } from '@atlaskit/editor-common/provider-factory';

import { MentionNodeComponentContext } from './mention-node-component-context';

export type MentionNodeComponentProps = {
	id: string;
	text: string;
	accessLevel?: string;
	userType?: string;
	localId?: string;
	providers?: ProviderFactory;
	eventHandlers?: EventHandlers;
};

/**
 * Renderer `mention` node component for sent human messages with a known
 * responding agent. Agent mentions borrow the people-mention visual language: the
 * responding agent is emphasised like your own (self) mention, and every other agent
 * is greyed out / disabled like an ordinary "someone else" mention. The responding
 * agent is tagged with `data-active-agent-mention` so the chat-bubble style override
 * can target it — CSS can't otherwise tell it apart from a default mention.
 */
export const MentionNodeComponent = ({
	id,
	text,
	accessLevel,
	userType,
	localId,
	providers,
	eventHandlers,
}: MentionNodeComponentProps): React.JSX.Element => {
	const context = React.useContext(MentionNodeComponentContext);
	const isRespondingAgent = !!context && userType === 'APP' && id === context.respondedAgentId;
	const isDisabled = !!context && userType === 'APP' && id !== context.respondedAgentId;
	const mention = (
		<EditorCommonMention
			id={id}
			text={text}
			accessLevel={accessLevel}
			localId={localId}
			providers={providers}
			eventHandlers={eventHandlers?.mention}
			isDisabled={isDisabled}
			disabledTooltip={isDisabled ? context.disabledTooltip : undefined}
		/>
	);
	return isRespondingAgent ? <span data-active-agent-mention>{mention}</span> : mention;
};
